import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  Nav,
  Row,
  Tab,
  Table,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { Heading, Icon } from '@lomarengas/design-system';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HubConnectionState } from '@microsoft/signalr';
import { SortableTable } from '../../common/components/SortableTable';
import { SectionHeader } from '../../common/components';
import { useAuthUser } from '../auth/authHooks';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  BookingsFilter,
  getPreBookingsAsync,
  getUnConfirmedBookingsAsync,
  getUnConfirmedInterhomeBookingsAsync,
  getUnConfirmedTravelBookingsAsync,
  getWrongBookingsAsync,
  selectPreBookingItems,
  selectUnConfirmedBookingItems,
  selectUnConfirmedInterhomeBookingItems,
  selectUnConfirmedTravelBookingItems,
  selectWrongBookingItems,
  setSearchFilters,
} from '../bookings/bookingsSlice';
import { setBookingErrorHandled } from '../../common/api/bookingsService';
import {
  BookingType,
  bookingTypeTranslations,
} from '../bookings/Constants/BookingTypeEnum';
import {
  getAllAccommodationsOfferedAsync,
  selectOfferedAccommodations,
} from '../accommodations/accommodationsSearchSlice';
import { checkRole } from '../auth/authSlice';
import { LOCALES, ROLES } from '../../common/constants';
import { useLocale } from '../../locales/useLocale';

function Index() {
  const { t } = useTranslation();
  const { currentLocale } = useLocale();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loginFirstName, roleClaims, identityClaims } = useAuthUser();

  const [time] = useState(new Date().getHours());
  const currentLanguage = currentLocale === LOCALES.Fi ? 'fi' : 'en';

  let greeting;
  if (time < 5) {
    greeting = t('dashboard:greetings.night');
  } else if (time < 9) {
    greeting = t('dashboard:greetings.morning');
  } else if (time < 12) {
    greeting = t('dashboard:greetings.lateMorning');
  } else if (time < 18) {
    greeting = t('dashboard:greetings.day');
  } else {
    greeting = t('dashboard:greetings.evening');
  }

  const isTravelAgencyUser = useAppSelector(checkRole([ROLES.TravelAgency]));
  const isTravelAgencyDomainUser = useAppSelector(
    checkRole([ROLES.TravelAgencyDomainUser]),
  );
  function getBookingTypeByName(name: string): BookingType | undefined {
    const bookingTypeNames = Object.keys(BookingType).filter(key =>
      Number.isNaN(Number(key)),
    );
    const foundType = bookingTypeNames.find(
      typeName => typeName.toLowerCase() === name.toLowerCase(),
    );
    return foundType
      ? BookingType[foundType as keyof typeof BookingType]
      : undefined;
  }
  function getBookingTypeTranslation(
    bookingType: string,
    locale: 'en' | 'fi',
  ): string {
    const selector = getBookingTypeByName(bookingType);
    return bookingTypeTranslations[locale][selector] || 'Unknown';
  }
  const initialBookingsFilter: BookingsFilter = {
    PageNumber: 1,
    PageSize: 1000000,
    searchString: '',
    authorId: '',
    bookingTypes: [],
    isCancelled: false,
    externalBookingSources: [],
    externalBookingsOnly: false,
    searchInBooking: false,
    searchInCustomer: false,
    searchInNotes: false,
    searchInAccommodation: false,
    searchInBilling: false,
    searchInAddon: false,
  };
  const [bookingsFilter, setBookingsFilter] = useState(initialBookingsFilter);
  const preBookings = useAppSelector(selectPreBookingItems);
  const unConfirmedBookings = useAppSelector(selectUnConfirmedBookingItems);
  const offeredAccommodations = useAppSelector(selectOfferedAccommodations);
  const unConfirmedInterhomeBookings = useAppSelector(
    selectUnConfirmedInterhomeBookingItems,
  );
  const wrongBookings = useAppSelector(selectWrongBookingItems);

  const unConfirmedTravelBookings = useAppSelector(
    selectUnConfirmedTravelBookingItems,
  );
  const getDefaultTab = () => {
    let tab = 'tab1';
    if (isTravelAgencyUser) tab = 'tab4';
    if (isTravelAgencyDomainUser) tab = 'tab2';
    return tab;
  };

  const urlParams = new URLSearchParams(window.location.search);
  const [activeTab, setActiveTab] = useState(getDefaultTab());
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const tab = urlParams.get('tab');
  useEffect(() => {
    if (tab) {
      setActiveTab(tab);
    }
  }, [tab]);

  const internetDatas = useMemo(
    () =>
      unConfirmedBookings?.map(item => ({
        id: item.id,
        Varaustunnus: item.bookingNumber,
        Alkupäivä: new Date(item.startDate).toString(),
        Loppupäivä: new Date(item.endDate).toString(),
        Tyyppi: getBookingTypeTranslation(item.bookingType, currentLanguage),
        Asiakas: `${item.payerFirstName} ${item.payerLastName}`,
        Majoitus: `${item.accommodationResponse.accommodationNumber} ${item.accommodationResponse.accommodationName}`,
        Virkailija: item.officerLastName,
        Luontipäivä: new Date(item.createdAt).toString(),
        Muokkaus: new Date(item.updatedAt).toString(),
        MaksuOnnistunut: item.onlinePaymentCompleted === true ? 'Kyllä' : 'Ei',
        Maksutapa: item.paymentMethod,
        isEdited: item.isEdited,
        isCancelled: item.isCancelled,
        isConfirmed: item.isConfirmed,
      })),
    [currentLanguage, unConfirmedBookings],
  );
  const preBookingDatas = useMemo(
    () =>
      preBookings?.map(item => ({
        id: item.id,
        Varaustunnus: item.bookingNumber,
        Alkupäivä: new Date(item.startDate).toString(),
        Loppupäivä: new Date(item.endDate).toString(),
        Tyyppi: getBookingTypeTranslation(item.bookingType, currentLanguage),
        Asiakas: `${item.payerFirstName} ${item.payerLastName}`,
        Majoitus: item.accommodationResponse
          ? `${item.accommodationResponse.accommodationNumber > 0 ? item.accommodationResponse.accommodationNumber : ''} ${item.accommodationResponse.accommodationName}`
          : '',
        Virkailija: item.officerLastName,
        Luontipäivä: new Date(item.createdAt).toString(),
        Muokkaus: new Date(item.updatedAt).toString(),
        Voimassa: item.preliminaryBookingDueDate
          ? new Date(item.preliminaryBookingDueDate).toString()
          : '',
        isDueDateGone: item.isDueDateGone,
        isEdited: item.isEdited,
        isCancelled: item.isCancelled,
        isConfirmed: item.isConfirmed,
        Lisätiedot: item.additionalInformation,
      })),
    [currentLanguage, preBookings],
  );

  const interhomeDatas = useMemo(
    () =>
      unConfirmedInterhomeBookings?.map(item => ({
        id: item.id,
        Varaustunnus: item.bookingNumber,
        Alkupäivä: new Date(item.startDate).toString(),
        Loppupäivä: new Date(item.endDate).toString(),
        Tyyppi: getBookingTypeTranslation(item.bookingType, currentLanguage),
        Asiakas: `${item.payerFirstName} ${item.payerLastName}`,
        Majoitus: `${item.accommodationResponse.accommodationNumber} ${item.accommodationResponse.accommodationName}`,
        Virkailija: item.officerLastName,
        Luontipäivä: new Date(item.createdAt).toString(),
        Muokkaus: new Date(item.updatedAt).toString(),
        isEdited: item.isEdited,
        isCancelled: item.isCancelled,
        isConfirmed: item.isConfirmed,
      })),
    [currentLanguage, unConfirmedInterhomeBookings],
  );
  const wrongDatas = useMemo(
    () =>
      wrongBookings?.map(item => ({
        id: item.id,
        Varaustunnus: item.bookingNumber,
        Alkupäivä: new Date(item.startDate).toString(),
        Loppupäivä: new Date(item.endDate).toString(),
        Tyyppi: getBookingTypeTranslation(item.bookingType, currentLanguage),
        Asiakas: `${item.payerFirstName} ${item.payerLastName}`,
        Majoitus: `${item.accommodationResponse.accommodationNumber} ${item.accommodationResponse.accommodationName}`,
        Virkailija: item.officerLastName,
        Luontipäivä: new Date(item.createdAt).toString(),
        Muokkaus: new Date(item.updatedAt).toString(),
        isEdited: item.isEdited,
        isCancelled: item.isCancelled,
        isConfirmed: item.isConfirmed,
        Lisätiedot: item.additionalInformation,
      })),
    [currentLanguage, wrongBookings],
  );
  const travelDatas = useMemo(
    () =>
      unConfirmedTravelBookings?.map(item => ({
        id: item.id,
        Varaustunnus:
          item.groupedBookingNumber !== null
            ? item.groupedBookingNumber
            : item.bookingNumber,
        Alkupäivä: new Date(item.startDate).toString(),
        Loppupäivä: new Date(item.endDate).toString(),
        Tyyppi: getBookingTypeTranslation(item.bookingType, currentLanguage),
        Asiakas: item.finalClient
          ? `${item.finalClient}`
          : `${item.payerFirstName} ${item.payerLastName}`,
        Lisätiedot: item.additionalInformation
          ? item.additionalInformation
          : `${item.payerFirstName} ${item.payerLastName}`,
        Majoitus: `${item.accommodationResponse.accommodationNumber} ${item.accommodationResponse.accommodationName}`,
        Matkatoimisto: item.travelAgency || '',
        Virkailija: `${item.officerLastName}`,
        Luontipäivä: new Date(item.createdAt).toString(),
        Muokkaus: new Date(item.updatedAt).toString(),
        isEdited: item.isEdited,
        isCancelled: item.isCancelled,
        isConfirmed: item.isConfirmed,
        finalClient: item.finalClient,
      })),
    [
      currentLanguage,
      isTravelAgencyDomainUser,
      isTravelAgencyUser,
      unConfirmedTravelBookings,
    ],
  );
  function getBookingId(number: number) {
    const found = unConfirmedBookings.filter(
      item => item.bookingNumber === number,
    );
    if (found.length > 0) {
      return found[0].id;
    }
    return null;
  }
  const markAsHandled = useCallback(
    async (id: string | number) => {
      try {
        await setBookingErrorHandled(id as string);
        const filter = {
          ...bookingsFilter,
          authorId: identityClaims.find(item => item.type === 'sub')?.value,
        } as BookingsFilter;
        dispatch(getWrongBookingsAsync(filter));
        setOpenDropdown(null);
      } catch (error) {
        console.error('Error marking booking as handled:', error);
      }
    },
    [bookingsFilter, dispatch, identityClaims],
  );
  const renderTable = useMemo(
    () => [
      {
        column: 'Varaustunnus',
        render: (value, column, columnIndex, data, dataIndex) => (
          <div>
            <span>{value}</span>
          </div>
        ),
      },
      {
        column: 'Alkupäivä',
        render: (value, column, columnIndex, data, dataIndex) => (
          <div>
            <span>{new Date(value).toLocaleDateString('fi-FI')}</span>
          </div>
        ),
      },
      {
        column: 'Loppupäivä',
        render: (value, column, columnIndex, data, dataIndex) => (
          <div>
            <span>{new Date(value).toLocaleDateString('fi-FI')}</span>
          </div>
        ),
      },
      {
        column: 'Luontipäivä',
        render: (value, column, columnIndex, data, dataIndex) => (
          <div>
            <span>{new Date(value).toLocaleDateString('fi-FI')}</span>
          </div>
        ),
      },
      {
        column: 'Muokkaus',
        render: (value, column, columnIndex, rowData, dataIndex) => (
          <div>
            <span>{new Date(value).toLocaleDateString('fi-FI')}</span>
          </div>
        ),
      },
      {
        column: 'Tila',
        render: (value, column, columnIndex, rowData, dataIndex) => {
          let badge;
          if (rowData.isCancelled) {
            badge = (
              <Badge bg="danger">
                {t('dashboard:bookings:item.cancelled')}
              </Badge>
            );
          } else if (rowData.isConfirmed) {
            badge = (
              <Badge bg="primary">
                {t('dashboard:bookings:item.confirmed')}
              </Badge>
            );
          } else if (rowData.isEdited) {
            badge = (
              <Badge bg="danger">
                {t('dashboard:bookings:item.inProgress')}
              </Badge>
            );
          } else {
            badge = (
              <Badge bg="info">
                {t('dashboard:bookings:item.unconfirmed')}
              </Badge>
            );
          }
          return <div className="ps-24">{badge}</div>;
        },
      },
      {
        column: 'Toiminnot',
        render: (_value, _column, _columnIndex, rowData) => (
          <div onClick={e => e.stopPropagation()} role="presentation">
            <Dropdown
              drop="up"
              show={openDropdown === rowData.id}
              onToggle={(show: boolean) =>
                setOpenDropdown(show ? rowData.id : null)
              }
            >
              <Dropdown.Toggle
                variant="link"
                id={`dropdown-${rowData.id}`}
                className="table-data-btn-actions"
              >
                <Icon AriaLabel="Toiminnot" Name="three-dots-vertical" />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={e => {
                    e.stopPropagation();
                    markAsHandled(rowData.id);
                  }}
                >
                  Merkitse käsitellyksi
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        ),
      },
    ],
    [t, markAsHandled, openDropdown],
  );
  const commonDateColumns = useMemo(
    () => ['Alkupäivä', 'Loppupäivä', 'Luontipäivä', 'Muokkaus'],
    [],
  );
  const preBookingDateColumns = useMemo(
    () => [...commonDateColumns, 'Voimassa'],
    [commonDateColumns],
  );
  const noSortColumns = useMemo(() => ['id'], []);
  const internetColumns = useMemo(
    () => [
      { value: 'Varaustunnus', label: 'Varaustunnus' },
      { value: 'Alkupäivä', label: 'Alkupäivä' },
      { value: 'Loppupäivä', label: 'Loppupäivä' },
      { value: 'Tyyppi', label: 'Tyyppi' },
      { value: 'Asiakas', label: 'Asiakas' },
      { value: 'Majoitus', label: 'Majoitus' },
      { value: 'Virkailija', label: 'Virkailija' },
      { value: 'Luontipäivä', label: 'Luontipäivä' },
      { value: 'Muokkaus', label: 'Muokkaus' },
      { value: 'MaksuOnnistunut', label: 'Maksu onnistunut' },
      { value: 'Maksutapa', label: 'Maksutapa' },
      { value: 'Tila', label: 'Käsittelyn tila' },
    ],
    [],
  );
  const preBookingColumns = useMemo(
    () => [
      { value: 'Varaustunnus', label: 'Varaustunnus' },
      { value: 'Alkupäivä', label: 'Alkupäivä' },
      { value: 'Loppupäivä', label: 'Loppupäivä' },
      { value: 'Tyyppi', label: 'Tyyppi' },
      { value: 'Asiakas', label: 'Asiakas' },
      { value: 'Majoitus', label: 'Majoitus' },
      { value: 'Lisätiedot', label: 'Lisätiedot' },
      { value: 'Luontipäivä', label: 'Luontipäivä' },
      { value: 'Voimassa', label: 'Voimassa' },
      { value: 'Tila', label: 'Käsittelyn tila' },
    ],
    [],
  );
  const interhomeColumns = useMemo(
    () => [
      { value: 'Varaustunnus', label: 'Varaustunnus' },
      { value: 'Alkupäivä', label: 'Alkupäivä' },
      { value: 'Loppupäivä', label: 'Loppupäivä' },
      { value: 'Tyyppi', label: 'Tyyppi' },
      { value: 'Asiakas', label: 'Asiakas' },
      { value: 'Majoitus', label: 'Majoitus' },
      { value: 'Virkailija', label: 'Virkailija' },
      { value: 'Luontipäivä', label: 'Luontipäivä' },
      { value: 'Muokkaus', label: 'Muokkaus' },
      { value: 'Tila', label: 'Käsittelyn tila' },
    ],
    [],
  );
  const travelColumns = useMemo(
    () => [
      {
        value: 'Varaustunnus',
        label: isTravelAgencyUser ? 'Booking Number' : 'Varaustunnus',
      },
      {
        value: 'Alkupäivä',
        label: isTravelAgencyUser ? 'Start date' : 'Alkupäivä',
      },
      {
        value: 'Loppupäivä',
        label: isTravelAgencyUser ? 'End date' : 'Loppupäivä',
      },
      { value: 'Tyyppi', label: isTravelAgencyUser ? 'Type' : 'Tyyppi' },
      { value: 'Asiakas', label: isTravelAgencyUser ? 'Customer' : 'Asiakas' },
      ...(isTravelAgencyDomainUser && !isTravelAgencyUser
        ? [
            {
              value: 'Matkatoimisto',
              label: 'Matkatoimisto',
            },
          ]
        : []),
      ...(isTravelAgencyDomainUser && !isTravelAgencyUser
        ? [
            {
              value: 'Lisätiedot',
              label: 'Lisätiedot',
            },
          ]
        : []),
      {
        value: 'Majoitus',
        label: isTravelAgencyUser ? 'Accommodation' : 'Majoitus',
      },
      {
        value: 'Virkailija',
        label: isTravelAgencyUser ? 'Officer' : 'Virkailija',
      },
      {
        value: 'Luontipäivä',
        label: isTravelAgencyUser ? 'Created date' : 'Luontipäivä',
      },
      { value: 'Muokkaus', label: isTravelAgencyUser ? 'Updated' : 'Muokkaus' },
      {
        value: 'Tila',
        label: isTravelAgencyUser ? 'Processing status' : 'Käsittelyn tila',
      },
    ],
    [isTravelAgencyUser],
  );
  const preBookingColumnRender = useMemo(
    () => [
      ...renderTable,
      {
        column: 'Voimassa',
        render: (value, column, columnIndex, data, dataIndex) => (
          <div>
            <span>
              {value ? new Date(value).toLocaleDateString('fi-FI') : ''}
            </span>
          </div>
        ),
      },
    ],
    [renderTable],
  );
  const internetAddProps = useMemo(
    () => ({
      tBodyRow: rowData => ({
        onClick: () => navigate(`/bookings/${rowData.id}`),
        style: { cursor: 'pointer' },
        className: rowData.isEdited ? 'bg-warning bg-opacity-25' : '',
      }),
    }),
    [navigate],
  );
  const preBookingAddProps = useMemo(
    () => ({
      tHeading: col =>
        col.value === 'Majoitus'
          ? { style: { width: '300px', maxWidth: '250px' } }
          : {},
      tData: (value, col) =>
        col.value === 'Majoitus'
          ? { style: { width: '300px', maxWidth: '250px' } }
          : {},
      tBodyRow: rowData => ({
        onClick: () =>
          navigate(`/bookings/${rowData.id}`, {
            state: { backUrl: '/?tab=tab2' },
          }),
        style: { cursor: 'pointer' },
        className: rowData.isDueDateGone ? 'bg-danger-subtle' : '',
      }),
    }),
    [navigate],
  );
  const interhomeAddProps = useMemo(
    () => ({
      tBodyRow: rowData => ({
        onClick: () =>
          navigate(`/bookings/${rowData.id}`, {
            state: { backUrl: '/?tab=tab3' },
          }),
        style: { cursor: 'pointer' },
        className: rowData.isEdited ? 'bg-warning' : '',
      }),
    }),
    [navigate],
  );
  const travelAddProps = useMemo(
    () => ({
      tBodyRow: rowData => ({
        onClick: () =>
          navigate(`/bookings/${rowData.id}`, {
            state: { backUrl: '/?tab=tab4' },
          }),
        style: { cursor: 'pointer' },
        className: rowData.isEdited ? 'bg-warning' : '',
      }),
    }),
    [navigate],
  );
  const [resetKey, setResetKey] = useState(0);
  const [internetData, setInternetData] = useState(internetDatas);
  const [preBookingData, setPreBookingData] = useState(preBookingDatas);
  const [interhomeData, setInterhomeData] = useState(interhomeDatas);
  const [travelData, setTravelData] = useState(travelDatas);
  const [wrongData, setWrongData] = useState(wrongDatas);

  useEffect(() => {
    setInternetData(internetDatas);
  }, [internetDatas]);

  useEffect(() => {
    setPreBookingData(preBookingDatas);
  }, [preBookingDatas]);

  useEffect(() => {
    setInterhomeData(interhomeDatas);
  }, [interhomeDatas]);

  useEffect(() => {
    setTravelData(travelDatas);
  }, [travelDatas]);
  useEffect(() => {
    setWrongData(wrongDatas);
  }, [wrongDatas]);
  const unConfirmedBookingListItems = unConfirmedBookings?.map(item => (
    <tr
      key={item.id}
      className={item.isEdited ? 'bg-warning bg-opacity-25' : ''}
      onClick={() =>
        navigate(`/bookings/${item.id}`, { state: { backUrl: '/?tab=tab1' } })
      }
      style={{ cursor: 'pointer' }}
    >
      {item.isEdited ? (
        <>
          <td>
            <span className="d-block">{item.bookingNumber}</span>
          </td>
          <td>
            <span className="d-block">
              {new Date(Date.parse(item.startDate)).toLocaleDateString(
                'fi-FI',
                {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                },
              )}{' '}
              -{' '}
              {new Date(Date.parse(item.endDate)).toLocaleDateString('fi-FI', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              })}
            </span>
          </td>
          <td>
            <span className="d-block">
              {getBookingTypeTranslation(item.bookingType, currentLanguage)}
            </span>
          </td>
          <td>
            <span className="d-block">
              {item.payerFirstName} {item.payerLastName}
            </span>
          </td>
          <td>
            <span className="nav-link">
              {item.accommodationResponse.accommodationNumber}{' '}
              {item.accommodationResponse.accommodationName}
            </span>
          </td>
          <td>
            <span className="nav-link">{item.officerLastName}</span>
          </td>
          <td>
            <span className="nav-link">
              {new Date(item.createdAt).toLocaleDateString('fi-FI')}
            </span>
          </td>
          <td>
            <span className="nav-link">
              {item.updatedAt
                ? new Date(item.updatedAt).toLocaleDateString('fi-FI')
                : ''}
            </span>
          </td>
          <td>
            <span className="nav-link">
              {item.onlinePaymentCompleted === true ? 'Kyllä' : 'Ei'}
            </span>
          </td>
          <td>
            <span className="nav-link">
              {item.paymentMethod !== null && item.paymentMethod !== undefined
                ? item.paymentMethod
                : ''}
            </span>
          </td>
          <td>
            {(() => {
              let badge;
              if (item.isCancelled) {
                badge = (
                  <span className="d-block">
                    <Badge bg="danger">
                      {t('dashboard:bookings:item.cancelled')}
                    </Badge>
                  </span>
                );
              } else if (item.isConfirmed) {
                badge = (
                  <span className="d-block">
                    <Badge bg="primary">
                      {t('dashboard:bookings:item.confirmed')}
                    </Badge>
                  </span>
                );
              } else if (item.isEdited) {
                badge = (
                  <span className="d-block">
                    <Badge bg="danger">
                      {t('dashboard:bookings:item.inProgress')}
                    </Badge>
                  </span>
                );
              } else {
                badge = (
                  <span className="d-block">
                    <Badge bg="info">
                      {t('dashboard:bookings:item.unconfirmed')}
                    </Badge>
                  </span>
                );
              }
              return badge;
            })()}
          </td>
        </>
      ) : (
        <>
          <td>
            <span className="d-block">{item.bookingNumber}</span>
          </td>
          <td>
            <span className="d-block">
              {new Date(Date.parse(item.startDate)).toLocaleDateString(
                'fi-FI',
                {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                },
              )}{' '}
              -{' '}
              {new Date(Date.parse(item.endDate)).toLocaleDateString('fi-FI', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              })}
            </span>
          </td>
          <td>
            <span className="d-block">
              {getBookingTypeTranslation(item.bookingType, currentLanguage)}
            </span>
          </td>
          <td>
            <span className="d-block">
              {item.payerFirstName} {item.payerLastName}
            </span>
          </td>
          <td>
            <span className="nav-link">
              {item.accommodationResponse.accommodationNumber}{' '}
              {item.accommodationResponse.accommodationName}
            </span>
          </td>
          <td>
            <span className="nav-link">{item.officerLastName}</span>
          </td>
          <td>
            <span className="nav-link">
              {new Date(item.createdAt).toLocaleDateString('fi-FI')}
            </span>
          </td>
          <td>
            <span className="nav-link">
              {item.updatedAt
                ? new Date(item.updatedAt).toLocaleDateString('fi-FI')
                : ''}
            </span>
          </td>
          <td>
            <span className="nav-link">
              {item.onlinePaymentCompleted === true ? 'Kyllä' : 'Ei'}
            </span>
          </td>
          <td>
            <span className="nav-link">
              {item.paymentMethod !== null && item.paymentMethod !== undefined
                ? item.paymentMethod
                : ''}
            </span>
          </td>
          <td>
            {(() => {
              let badge;
              if (item.isCancelled) {
                badge = (
                  <span className="d-block">
                    <Badge bg="danger">
                      {t('dashboard:bookings:item.cancelled')}
                    </Badge>
                  </span>
                );
              } else if (item.isConfirmed) {
                badge = (
                  <span className="d-block">
                    <Badge bg="primary">
                      {t('dashboard:bookings:item.confirmed')}
                    </Badge>
                  </span>
                );
              } else if (item.isEdited) {
                badge = (
                  <span className="d-block">
                    <Badge bg="danger">
                      {t('dashboard:bookings:item.inProgress')}
                    </Badge>
                  </span>
                );
              } else {
                badge = (
                  <span className="d-block">
                    <Badge bg="info">
                      {t('dashboard:bookings:item.unconfirmed')}
                    </Badge>
                  </span>
                );
              }
              return badge;
            })()}
          </td>
        </>
      )}
    </tr>
  ));

  const unConfirmedInterhomeBookingListItems =
    unConfirmedInterhomeBookings?.map(item => (
      <tr
        key={item.id}
        className={item.isEdited ? 'bg-warning' : ''}
        onClick={() =>
          navigate(`/bookings/${item.id}`, { state: { backUrl: '/?tab=tab3' } })
        }
        style={{ cursor: 'pointer' }}
      >
        {item.isEdited ? (
          <>
            <td>
              <span className="d-block">{item.bookingNumber}</span>
            </td>
            <td>
              <span className="d-block">
                {new Date(Date.parse(item.startDate)).toLocaleDateString(
                  'fi-FI',
                  {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  },
                )}{' '}
                -{' '}
                {new Date(Date.parse(item.endDate)).toLocaleDateString(
                  'fi-FI',
                  {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  },
                )}
              </span>
            </td>
            <td>
              <span className="d-block">
                {getBookingTypeTranslation(item.bookingType, currentLanguage)}
              </span>
            </td>
            <td>
              <span className="d-block">
                {item.payerFirstName} {item.payerLastName}
              </span>
            </td>
            <td>
              <span className="nav-link">
                {item.accommodationResponse.accommodationNumber}{' '}
                {item.accommodationResponse.accommodationName}
              </span>
            </td>
            <td>
              <span className="nav-link">{item.officerLastName}</span>
            </td>
            <td>
              <span className="nav-link">
                {new Date(item.createdAt).toLocaleDateString('fi-FI')}
              </span>
            </td>
            <td>
              <span className="nav-link">
                {item.updatedAt
                  ? new Date(item.updatedAt).toLocaleDateString('fi-FI')
                  : ''}
              </span>
            </td>
            <td>
              {(() => {
                let badge;
                if (item.isCancelled) {
                  badge = (
                    <span className="d-block">
                      <Badge bg="danger">
                        {t('dashboard:bookings:item.cancelled')}
                      </Badge>
                    </span>
                  );
                } else if (item.isConfirmed) {
                  badge = (
                    <span className="d-block">
                      <Badge bg="primary">
                        {t('dashboard:bookings:item.confirmed')}
                      </Badge>
                    </span>
                  );
                } else if (item.isEdited) {
                  badge = (
                    <span className="d-block">
                      <Badge bg="danger">
                        {t('dashboard:bookings:item.inProgress')}
                      </Badge>
                    </span>
                  );
                } else {
                  badge = (
                    <span className="d-block">
                      <Badge bg="info">
                        {t('dashboard:bookings:item.unconfirmed')}
                      </Badge>
                    </span>
                  );
                }
                return badge;
              })()}
            </td>
          </>
        ) : (
          <>
            <td>
              <span className="d-block">{item.bookingNumber}</span>
            </td>
            <td>
              <span className="d-block">
                {new Date(Date.parse(item.startDate)).toLocaleDateString(
                  'fi-FI',
                  {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  },
                )}{' '}
                -{' '}
                {new Date(Date.parse(item.endDate)).toLocaleDateString(
                  'fi-FI',
                  {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  },
                )}
              </span>
            </td>
            <td>
              <span className="d-block">
                {getBookingTypeTranslation(item.bookingType, currentLanguage)}
              </span>
            </td>
            <td>
              <span className="d-block">
                {item.payerFirstName} {item.payerLastName}
              </span>
            </td>
            <td>
              <span className="nav-link">
                {item.accommodationResponse.accommodationNumber}{' '}
                {item.accommodationResponse.accommodationName}
              </span>
            </td>
            <td>
              <span className="nav-link">{item.officerLastName}</span>
            </td>
            <td>
              <span className="nav-link">
                {new Date(item.createdAt).toLocaleDateString('fi-FI')}
              </span>
            </td>
            <td>
              <span className="nav-link">
                {item.updatedAt
                  ? new Date(item.updatedAt).toLocaleDateString('fi-FI')
                  : ''}
              </span>
            </td>
            <td>
              {(() => {
                let badge;
                if (item.isCancelled) {
                  badge = (
                    <span className="d-block">
                      <Badge bg="danger">
                        {t('dashboard:bookings:item.cancelled')}
                      </Badge>
                    </span>
                  );
                } else if (item.isConfirmed) {
                  badge = (
                    <span className="d-block">
                      <Badge bg="primary">
                        {t('dashboard:bookings:item.confirmed')}
                      </Badge>
                    </span>
                  );
                } else if (item.isEdited) {
                  badge = (
                    <span className="d-block">
                      <Badge bg="danger">
                        {t('dashboard:bookings:item.inProgress')}
                      </Badge>
                    </span>
                  );
                } else {
                  badge = (
                    <span className="d-block">
                      <Badge bg="info">
                        {t('dashboard:bookings:item.unconfirmed')}
                      </Badge>
                    </span>
                  );
                }
                return badge;
              })()}
            </td>
          </>
        )}
      </tr>
    ));

  const unConfirmedTravelBookingListItems = unConfirmedTravelBookings?.map(
    item => (
      <tr
        key={item.id}
        className={item.isEdited ? 'bg-warning' : ''}
        onClick={() =>
          navigate(`/bookings/${item.id}`, { state: { backUrl: '/?tab=tab4' } })
        }
        style={{ cursor: 'pointer' }}
      >
        {item.isEdited ? (
          <>
            <td>
              {item.groupedBookingNumber !== null && (
                <span className="d-block">{item.groupedBookingNumber}</span>
              )}
              {item.groupedBookingNumber === null && (
                <span className="d-block">{item.bookingNumber}</span>
              )}
            </td>
            <td>
              <span className="d-block">
                {new Date(Date.parse(item.startDate)).toLocaleDateString(
                  'fi-FI',
                  {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  },
                )}{' '}
                -{' '}
                {new Date(Date.parse(item.endDate)).toLocaleDateString(
                  'fi-FI',
                  {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  },
                )}
              </span>
            </td>
            <td>
              <span className="d-block">
                {getBookingTypeTranslation(item.bookingType, currentLanguage)}
              </span>
            </td>
            <td>
              <span className="d-block">
                {item.payerFirstName} {item.payerLastName}
              </span>
            </td>
            <td>
              <span className="nav-link">
                {item.accommodationResponse.accommodationNumber}{' '}
                {item.accommodationResponse.accommodationName}
              </span>
            </td>
            <td>
              <span className="nav-link">{item.officerLastName}</span>
            </td>
            <td>
              <span className="nav-link">
                {new Date(item.createdAt).toLocaleDateString('fi-FI')}
              </span>
            </td>
            <td>
              <span className="nav-link">
                {item.updatedAt
                  ? new Date(item.updatedAt).toLocaleDateString('fi-FI')
                  : ''}
              </span>
            </td>
            <td>
              {(() => {
                let badge;
                if (item.isCancelled) {
                  badge = (
                    <span className="d-block">
                      <Badge bg="danger">
                        {t('dashboard:bookings:item.cancelled')}
                      </Badge>
                    </span>
                  );
                } else if (item.isConfirmed) {
                  badge = (
                    <span className="d-block">
                      <Badge bg="primary">
                        {t('dashboard:bookings:item.confirmed')}
                      </Badge>
                    </span>
                  );
                } else if (item.isEdited) {
                  badge = (
                    <span className="d-block">
                      <Badge bg="danger">
                        {t('dashboard:bookings:item.inProgress')}
                      </Badge>
                    </span>
                  );
                } else {
                  badge = (
                    <span className="d-block">
                      <Badge bg="info">
                        {t('dashboard:bookings:item.unconfirmed')}
                      </Badge>
                    </span>
                  );
                }
                return badge;
              })()}
            </td>
          </>
        ) : (
          <>
            <td>
              {item.groupedBookingNumber !== null && (
                <span className="d-block">{item.groupedBookingNumber}</span>
              )}
              {item.groupedBookingNumber === null && (
                <span className="d-block">{item.bookingNumber}</span>
              )}
            </td>
            <td>
              <span className="d-block">
                {new Date(Date.parse(item.startDate)).toLocaleDateString(
                  'fi-FI',
                  {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  },
                )}{' '}
                -{' '}
                {new Date(Date.parse(item.endDate)).toLocaleDateString(
                  'fi-FI',
                  {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  },
                )}
              </span>
            </td>
            <td>
              <span className="d-block">
                {getBookingTypeTranslation(item.bookingType, currentLanguage)}
              </span>
            </td>
            <td>
              <span className="d-block">
                {item.payerFirstName} {item.payerLastName}
              </span>
            </td>
            <td>
              <span className="nav-link">
                {item.accommodationResponse.accommodationNumber}{' '}
                {item.accommodationResponse.accommodationName}
              </span>
            </td>
            <td>
              <span className="nav-link">{item.officerLastName}</span>
            </td>
            <td>
              <span className="nav-link">
                {new Date(item.createdAt).toLocaleDateString('fi-FI')}
              </span>
            </td>
            <td>
              <span className="nav-link">
                {item.updatedAt
                  ? new Date(item.updatedAt).toLocaleDateString('fi-FI')
                  : ''}
              </span>
            </td>
            <td>
              {(() => {
                let badge;
                if (item.isCancelled) {
                  badge = (
                    <span className="d-block">
                      <Badge bg="danger">
                        {t('dashboard:bookings:item.cancelled')}
                      </Badge>
                    </span>
                  );
                } else if (item.isConfirmed) {
                  badge = (
                    <span className="d-block">
                      <Badge bg="primary">
                        {t('dashboard:bookings:item.confirmed')}
                      </Badge>
                    </span>
                  );
                } else if (item.isEdited) {
                  badge = (
                    <span className="d-block">
                      <Badge bg="danger">
                        {t('dashboard:bookings:item.inProgress')}
                      </Badge>
                    </span>
                  );
                } else {
                  badge = (
                    <span className="d-block">
                      <Badge bg="info">
                        {t('dashboard:bookings:item.unconfirmed')}
                      </Badge>
                    </span>
                  );
                }
                return badge;
              })()}
            </td>
          </>
        )}
      </tr>
    ),
  );

  const bookingListItems = preBookings?.map(item => (
    <tr
      key={item.id}
      className={item.isDueDateGone ? 'bg-danger-subtle' : ''}
      onClick={() =>
        navigate(`/bookings/${item.id}`, { state: { backUrl: '/?tab=tab2' } })
      }
      style={{ cursor: 'pointer' }}
    >
      <td>
        <span className="d-block">{item.bookingNumber}</span>
      </td>
      <td>
        <span className="d-block">
          {new Date(Date.parse(item.startDate)).toLocaleDateString('fi-FI', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })}{' '}
          -{' '}
          {new Date(Date.parse(item.endDate)).toLocaleDateString('fi-FI', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })}
        </span>
      </td>
      <td>
        <span className="d-block">
          {getBookingTypeTranslation(item.bookingType, currentLanguage)}
        </span>
      </td>
      <td>
        <span className="d-block">
          {item.payerFirstName} {item.payerLastName}
        </span>
      </td>
      {item.accommodationResponse !== null && (
        <td>
          <span className="nav-link">
            {item.accommodationResponse.accommodationNumber}{' '}
            {item.accommodationResponse.accommodationName}
          </span>
        </td>
      )}
      <td>
        <span className="nav-link">{item.officerLastName}</span>
      </td>
      <td>
        <span className="nav-link">
          {new Date(item.createdAt).toLocaleDateString('fi-FI')}
        </span>
      </td>
      <td>
        <span className="nav-link">
          {item.updatedAt
            ? new Date(item.updatedAt).toLocaleDateString('fi-FI')
            : ''}
        </span>
      </td>
      <td>
        <span className="nav-link">
          {item.preliminaryBookingDueDate
            ? new Date(item.preliminaryBookingDueDate ?? '').toLocaleDateString(
                'fi-FI',
              )
            : ''}
        </span>
      </td>
      <td>
        {(() => {
          let badge;
          if (item.isCancelled) {
            badge = (
              <Badge bg="danger">
                {t('dashboard:bookings:item.cancelled')}
              </Badge>
            );
          } else if (item.isConfirmed) {
            badge = (
              <Badge bg="primary">
                {t('dashboard:bookings:item.confirmed')}
              </Badge>
            );
          } else if (item.isEdited) {
            badge = (
              <Badge bg="danger">
                {t('dashboard:bookings:item.inProgress')}
              </Badge>
            );
          } else {
            badge = (
              <Badge bg="info">
                {t('dashboard:bookings:item.unconfirmed')}
              </Badge>
            );
          }
          return badge;
        })()}
      </td>
    </tr>
  ));

  const offeredAccommodationItems = offeredAccommodations?.map(item => (
    <tr key={item.id}>
      <td>
        <Link
          className="booking-link"
          to={`/accommodations/details/${item.id}`}
          state={{ backUrl: '/?tab=tab5' }}
          target="_blank"
        >
          {item.accommodationNumber} {item.name}
        </Link>
      </td>
    </tr>
  ));
  const doSearch = () => {
    const filter = {
      ...bookingsFilter,
      authorId: identityClaims.find(item => item.type === 'sub')?.value,
    } as BookingsFilter;
    setBookingsFilter(filter);
    if (!isTravelAgencyUser && !isTravelAgencyDomainUser) {
      dispatch(getPreBookingsAsync(filter));
      dispatch(getUnConfirmedBookingsAsync(filter));
      dispatch(getUnConfirmedInterhomeBookingsAsync(filter));
      dispatch(getUnConfirmedTravelBookingsAsync(filter));
      dispatch(getWrongBookingsAsync(filter));
      dispatch(getAllAccommodationsOfferedAsync());
    }
    if (isTravelAgencyDomainUser) {
      dispatch(getPreBookingsAsync(filter));
      dispatch(getUnConfirmedTravelBookingsAsync(filter));
    }
    if (isTravelAgencyUser) {
      dispatch(getUnConfirmedTravelBookingsAsync(filter));
    }
  };
  useEffect(() => {
    doSearch();
  }, []);

  const resetOrder = () => {
    setInternetData(internetDatas);
    setPreBookingData(preBookingDatas);
    setInterhomeData(interhomeDatas);
    setTravelData(travelDatas);
    setWrongData(wrongDatas);
    setResetKey(k => k + 1);
  };

  return (
    <>
      <Helmet>
        <title>{t('dashboard:title')}</title>
      </Helmet>
      <Container className="container-content">
        <div className="pb-24">
          <SectionHeader
            Breadcrumbs={[t('dashboard:header.breadcrumb')]}
            Title={`${greeting}, ${loginFirstName ?? ''}!`}
          />
        </div>

        <Heading Level={4} Title={t('dashboard:bookings.title')} />

        <Tab.Container
          id="left-tabs-example"
          activeKey={activeTab}
          onSelect={changeTab => {
            setActiveTab(changeTab);
          }}
        >
          <div className="overflow-hidden mb-16 mt-16">
            <Nav variant="tabs">
              {!isTravelAgencyUser && !isTravelAgencyDomainUser && (
                <Nav.Item>
                  <Nav.Link eventKey="tab1">
                    Internet
                    <span className="badge badge-sm bg-primary p-4 ms-8 align-middle">
                      {unConfirmedBookings.length}
                    </span>
                  </Nav.Link>
                </Nav.Item>
              )}
              {!isTravelAgencyUser && (
                <Nav.Item>
                  <Nav.Link
                    eventKey="tab2"
                    style={{
                      backgroundColor: preBookings?.some(b => b.isDueDateGone)
                        ? '#ffcdd2'
                        : '',
                    }}
                  >
                    Alustavat
                    <span className="badge badge-sm bg-primary p-4 ms-8 align-middle">
                      {preBookings.length}
                    </span>
                  </Nav.Link>
                </Nav.Item>
              )}
              {!isTravelAgencyUser && !isTravelAgencyDomainUser && (
                <Nav.Item>
                  <Nav.Link eventKey="tab3">
                    Interhome
                    <span className="badge badge-sm bg-primary p-4 ms-8 align-middle">
                      {unConfirmedInterhomeBookings.length}
                    </span>
                  </Nav.Link>
                </Nav.Item>
              )}
              <Nav.Item>
                <Nav.Link eventKey="tab4">
                  {t('dashboard:bookings.travelagentsbookings')}
                  <span className="badge badge-sm bg-primary p-4 ms-8 align-middle">
                    {unConfirmedTravelBookings.length}
                  </span>
                </Nav.Link>
              </Nav.Item>
              {!isTravelAgencyUser && !isTravelAgencyDomainUser && (
                <Nav.Item>
                  <Nav.Link eventKey="tab5">
                    Tarjottu myyntiin
                    <span className="badge badge-sm bg-primary p-4 ms-8 align-middle">
                      {offeredAccommodations !== undefined
                        ? offeredAccommodations.length
                        : ''}
                    </span>
                  </Nav.Link>
                </Nav.Item>
              )}
              {!isTravelAgencyUser && !isTravelAgencyDomainUser && (
                <Nav.Item>
                  <Nav.Link
                    className={`text-danger${wrongBookings && wrongBookings.length > 0 ? ' bg-danger bg-opacity-10 rounded' : ''}`}
                    eventKey="tab6"
                  >
                    Virheelliset varaukset
                    <span className="badge badge-sm bg-danger p-4 ms-8 align-middle">
                      {wrongBookings !== undefined ? wrongBookings.length : ''}
                    </span>
                  </Nav.Link>
                </Nav.Item>
              )}
            </Nav>
          </div>
          <Card className="mb-48">
            <Tab.Content>
              <Tab.Pane eventKey="tab1">
                <div className="table-responsive">
                  <div className="table-responsive-lg">
                    <Button
                      variant="outline-primary"
                      className="p-0"
                      onClick={() => resetOrder()}
                    >
                      Nollaa järjestys
                    </Button>
                    <SortableTable
                      key={resetKey}
                      data={internetData}
                      setData={setInternetData}
                      dateColumns={commonDateColumns}
                      noSortColumns={noSortColumns}
                      columns={internetColumns}
                      columnRender={renderTable}
                      addProps={internetAddProps}
                    />
                  </div>
                </div>
              </Tab.Pane>
              <Tab.Pane eventKey="tab2">
                <div className="table-responsive">
                  <div className="table-responsive-lg">
                    <Button
                      variant="outline-primary"
                      className="p-0"
                      onClick={() => resetOrder()}
                    >
                      Nollaa järjestys
                    </Button>
                    <SortableTable
                      key={resetKey}
                      data={preBookingData}
                      setData={setPreBookingData}
                      dateColumns={preBookingDateColumns}
                      noSortColumns={noSortColumns}
                      columns={preBookingColumns}
                      columnRender={preBookingColumnRender}
                      addProps={preBookingAddProps}
                    />
                  </div>
                </div>
              </Tab.Pane>
              <Tab.Pane eventKey="tab3">
                <div className="table-responsive">
                  <div className="table-responsive-lg">
                    <Button
                      variant="outline-primary"
                      className="p-0"
                      onClick={() => resetOrder()}
                    >
                      Nollaa järjestys
                    </Button>
                    <SortableTable
                      key={resetKey}
                      data={interhomeData}
                      setData={setInterhomeData}
                      dateColumns={commonDateColumns}
                      noSortColumns={noSortColumns}
                      columns={interhomeColumns}
                      columnRender={renderTable}
                      addProps={interhomeAddProps}
                    />
                  </div>
                </div>
              </Tab.Pane>
              <Tab.Pane eventKey="tab4">
                <div className="table-responsive">
                  <div className="table-responsive-lg">
                    {!isTravelAgencyUser && (
                      <Button
                        variant="outline-primary"
                        className="p-0"
                        onClick={() => resetOrder()}
                      >
                        Nollaa järjestys
                      </Button>
                    )}
                    <SortableTable
                      key={resetKey}
                      data={travelData}
                      setData={setTravelData}
                      dateColumns={commonDateColumns}
                      noSortColumns={noSortColumns}
                      columns={travelColumns}
                      columnRender={renderTable}
                      addProps={travelAddProps}
                    />
                  </div>
                </div>
              </Tab.Pane>
              <Tab.Pane eventKey="tab5">
                <div className="table-responsive">
                  <div className="table-responsive-lg">
                    <Table hover className="table-data">
                      <thead>
                        <tr>
                          <th>Kohde</th>
                        </tr>
                      </thead>
                      <tbody>{offeredAccommodationItems}</tbody>
                    </Table>
                  </div>
                </div>
              </Tab.Pane>
              <Tab.Pane eventKey="tab6">
                <div className="table-responsive">
                  <div className="table-responsive-lg">
                    <Button
                      variant="outline-primary"
                      className="p-0"
                      onClick={() => resetOrder()}
                    >
                      Nollaa järjestys
                    </Button>
                    <SortableTable
                      key={resetKey}
                      data={wrongData}
                      setData={setWrongData}
                      dateColumns={[
                        'Alkupäivä',
                        'Loppupäivä',
                        'Luontipäivä',
                        'Muokkaus',
                      ]}
                      noSortColumns={['id']}
                      columns={[
                        { value: 'Varaustunnus', label: 'Varaustunnus' },
                        { value: 'Alkupäivä', label: 'Alkupäivä' },
                        { value: 'Loppupäivä', label: 'Loppupäivä' },
                        { value: 'Tyyppi', label: 'Tyyppi' },
                        { value: 'Asiakas', label: 'Asiakas' },
                        { value: 'Majoitus', label: 'Majoitus' },
                        { value: 'Virkailija', label: 'Virkailija' },
                        { value: 'Luontipäivä', label: 'Luontipäivä' },
                        { value: 'Muokkaus', label: 'Muokkaus' },
                        { value: 'Lisätiedot', label: 'Lisätiedot' },
                        { value: 'Tila', label: 'Käsittelyn tila' },
                        { value: 'Toiminnot', label: 'Toiminnot' },
                      ]}
                      columnRender={renderTable}
                      addProps={{
                        tBodyRow: rowData => ({
                          onClick: () =>
                            navigate(`/bookings/${rowData.id}`, {
                              state: { backUrl: '/?tab=tab4' },
                            }),
                          style: { cursor: 'pointer' },
                          className: rowData.isEdited ? 'bg-warning' : '',
                        }),
                      }}
                    />
                  </div>
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Card>
        </Tab.Container>
        {/*
        <Heading Level={4} Title="Roolit testausta" />
        <Card className="mt-32">
          <Card.Body>
            {roleClaims.map(function (role: string, index: React.Key) {
              return <div key={encodeURIComponent(role)}>{role}</div>;
            })}
          </Card.Body>
        </Card>
        */}
      </Container>
    </>
  );
}

export default Index;
